import type { ReactNode } from 'react';
import { confirmDialog } from 'dialogs';

interface DeleteExecutorOptions {
  readonly refresh: () => void;
  readonly confirmTitle?: ReactNode;
  readonly confirmBody?: ReactNode;
}

/**
 * Build the execute handler for a delete action over a selection. Confirms
 * once, deletes every entity via `remove` (typically dispatching the type's
 * delete thunk), then refreshes. A definition's `resolve` decides
 * enabled/hidden; this is only the handler it returns when enabled.
 *
 * Generic over the concrete entity so each type passes its own narrowed shape;
 * the genericity is local to this factory, not in the shared contract.
 */
export const deleteExecutor = <E,>(entities: readonly E[], remove: (entity: E) => Promise<unknown>, options: DeleteExecutorOptions): (() => void) => {
  return () => {
    void (async () => {
      const confirmed = await confirmDialog({
        title: options.confirmTitle ?? 'Confirm Delete',
        body: options.confirmBody ?? `Are you sure you want to delete ${entities.length === 1 ? 'this item' : `these ${entities.length} items`}?`,
        confirmText: 'Delete',
        confirmVariant: 'danger',
      });
      if (!confirmed) return;
      await Promise.all(entities.map(remove));
      options.refresh();
    })();
  };
};
